import { cn } from '../../lib/utils';
import { Card } from './Card';

/**
 * Spójny empty state — używaj wszędzie gdzie lista/dashboard/tabela jest pusta.
 *
 * Props:
 *  - icon:        ReactNode (lucide icon, rozmiar w-7 h-7)
 *  - title:       string
 *  - description: string | ReactNode
 *  - action:      ReactNode — Button lub kilka, w prawym CTA
 *  - tone:        'neutral' | 'success' | 'info' | 'warning' | 'danger'
 *  - size:        'sm' | 'md' | 'lg'   — md domyślnie (py-16); lg dla dashboardów (py-20)
 *  - asCard:      bool — owinąć w <Card>; default true
 */
const TONE_CLASSES = {
  neutral: 'bg-ink-100 text-ink-400 dark:bg-ink-800 dark:text-ink-500',
  success: 'bg-success-light text-success-dark dark:bg-success/15 dark:text-success',
  info:    'bg-info-light text-info-dark dark:bg-info/15 dark:text-info',
  warning: 'bg-warning-light text-warning-dark dark:bg-warning/15 dark:text-warning',
  danger:  'bg-danger-light text-danger-dark dark:bg-danger/15 dark:text-danger',
};

const SIZE_PADDING = {
  sm: 'py-10 px-6',
  md: 'py-16 px-6',
  lg: 'py-20 px-6',
};

const ICON_SIZE = {
  sm: 'w-12 h-12',
  md: 'w-14 h-14',
  lg: 'w-16 h-16',
};

export default function EmptyState({
  icon,
  title,
  description,
  action,
  tone = 'neutral',
  size = 'md',
  asCard = true,
  className,
}) {
  const inner = (
    <div className={cn('text-center', SIZE_PADDING[size], className)}>
      {icon && (
        <div className={cn(
          'mx-auto rounded-2xl flex items-center justify-center mb-4',
          ICON_SIZE[size],
          TONE_CLASSES[tone],
        )}>
          {icon}
        </div>
      )}
      {title && (
        <h3 className="text-lg font-semibold text-ink-900 dark:text-ink-50 mb-1.5 tracking-tightish">
          {title}
        </h3>
      )}
      {description && (
        <p className="text-sm text-ink-500 dark:text-ink-400 max-w-md mx-auto text-pretty">
          {description}
        </p>
      )}
      {action && (
        <div className="mt-5 inline-flex items-center gap-2 justify-center flex-wrap">
          {action}
        </div>
      )}
    </div>
  );

  if (!asCard) return inner;
  return <Card className="overflow-hidden">{inner}</Card>;
}
